// TODO - Type 'any' needs to be fixed.
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Button, Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import makeStyles from "@mui/styles/makeStyles";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { formatDate } from "../../../../../utils/helpers";
import JobItem from "./../../job-role-item";

type Action =
	| { type: "selectShiftForPublish"; payload: number }
	| { type: "deselectShiftForPublish"; payload: number }
	| { type: "setShiftsForPublish"; payload: number[] };

const dayInMonthComparator = (v1: Date, v2: Date) =>
	v1.getTime() - v2.getTime();

interface ReviewPublishColumnsOptions {
	// Whether the user may pick Workers at all; drives the header hint
	hasDirectAssignPermission: boolean;
	canDirectAssign: (locationId: number | null | undefined) => boolean;
	assignedCountByShift: { [shiftId: number]: number };
	// Workers can be picked only for a Shift ticked for publishing
	selectedShiftIds: number[];
	onAssignWorkers: (shiftId: number) => void;
}

export const useReviewPublishColumns = ({
	hasDirectAssignPermission,
	canDirectAssign,
	assignedCountByShift,
	selectedShiftIds,
	onAssignWorkers,
}: ReviewPublishColumnsOptions) => {
	const { t } = useTranslation();
	return useMemo(
		() => [
	{
		field: "shiftTime",
		headerName: t("scheduler_shift-time"),
		minWidth: 320,
		flex: 1,
		valueGetter: (params: any) => new Date(params.row.shiftDateTime),
		sortComparator: dayInMonthComparator,
		renderCell: (params: any) => {
			const startTime = params.row.time.start;
			const endTime = params.row.time.end;
			const color = params.row.restrictFlexHours
				? !params.row.timeExceeded
					? "#747474"
					: "#bdbdbd"
				: "#747474";
			return (
				<div
					style={{
						display: "flex",
						padding: "10px",
						gap: "4px",
						alignItems: "center",
					}}
				>
					<Typography variant='subtitle2' sx={{ color: color }}>
						{formatDate({
							dateStr: params.row.shiftDateTime,
							formatType: "SHORT_DATE",
						})}
					</Typography>
					<Typography variant='body2' sx={{ color: color }}>
						{startTime} - {endTime}
					</Typography>
					{params.row.isUrgent && (
						<Box
							sx={{
								padding: "2px 8px",
								display: "inline-flex",
								flexDirection: "row",
								alignItems: "center",
								border: 1,
								borderWidth: "1px",
								borderColor: "#D32F2F",
								borderRadius: "4px",
								backgroundColor: "#FFEBEE",
								marginLeft: "4px",
							}}
						>
							<Typography
								sx={{
									fontFamily: "Roboto",
									color: "#D32F2F",
									fontSize: "12px",
									fontWeight: "500",
									lineHeight: "20px",
									letterSpacing: "0.4px",
								}}
							>
								{t("scheduler_urgent")}
							</Typography>
							<ErrorOutlineIcon
								sx={{
									color: "#D32F2F",
									fontSize: "16px",
									marginLeft: "4px",
								}}
							/>
						</Box>
					)}
				</div>
			);
		},
	},
	{
		field: "shiftPurpose",
		headerName: t("scheduler_shift-purpose"),
		minWidth: 150,
		flex: 1,
		valueGetter: (params: any) => params.row.shiftPurposeId,
		renderCell: (params: any) => {
			const color = params.row.restrictFlexHours
				? !params.row.timeExceeded
					? "#747474"
					: "#bdbdbd"
				: "#747474";
			return (
				<Typography variant='subtitle2' sx={{ color: color }}>
					{params.row.shiftPurpose}
				</Typography>
			);
		},
	},
	{
		field: "shiftPurposeDetail",
		headerName: t("scheduler_purpose-details"),
		minWidth: 180,
		flex: 1,
		valueGetter: (params: any) => params.row.shiftPurposeDetail,
		renderCell: (params: any) => {
			const color = params.row.restrictFlexHours
				? !params.row.timeExceeded
					? "#747474"
					: "#bdbdbd"
				: "#747474";
			const { shiftPurposeDetail } = params.row;
			// Rows without an answer show "-"; there is nothing more to reveal.
			const hasDetail = !!shiftPurposeDetail && shiftPurposeDetail !== "-";
			return (
				<Tooltip
					title={hasDetail ? shiftPurposeDetail : ""}
					disableInteractive
				>
					<Typography
						variant='subtitle2'
						sx={{
							color: color,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{params.row.shiftPurposeDetail}
					</Typography>
				</Tooltip>
			);
		},
	},
	{
		field: "jobRole",
		headerName: t("scheduler_job-role-skills-required"),
		minWidth: 240,
		flex: 1,
		renderCell: (params: any) => {
			const color = params.row.restrictFlexHours
				? !params.row.timeExceeded
					? "#747474"
					: "#bdbdbd"
				: "#747474";
			const shiftSkills = params.row.skills;
			if (!params.row.jobRole) {
				const length = shiftSkills.length;
				const skills = shiftSkills
					.map((skill: { skillName: string }) => skill.skillName)
					.join(", ");
				if (length === 1)
					return (
            <Tooltip title={skills} arrow disableInteractive>
              <Typography variant="subtitle2" sx={{ color: color }}>
                {skills}
              </Typography>
            </Tooltip>
          );
				return (
          <Tooltip title={skills} arrow disableInteractive>
            <Box sx={{ wordBreak: "break-word" }} pt={1} pb={1}>
              <Typography variant="subtitle2" sx={{ color: color }}>
                {` ${shiftSkills[0].skillName}, `}
                <span
                  style={{
                    color: "#747474",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >{t("scheduler_plus-skills", { n: shiftSkills.length - 1 })}</span>
              </Typography>
            </Box>
          </Tooltip>
        );
			}
			return <JobItem jobRole={params.row.jobRole} />;
		},
	},
	{
		field: "workersRequired",
		headerName: t("scheduler_required-workers"),
		minWidth: hasDirectAssignPermission ? 240 : 150,
		flex: hasDirectAssignPermission ? 1.5 : 1,
		renderHeader: () => (
			<Box display='flex' alignItems='center' gap={0.5}>
				<Typography variant='subtitle2' sx={{ fontWeight: 500 }}>
					{t("scheduler_required-workers")}
				</Typography>
				{hasDirectAssignPermission && (
					<Tooltip title={t("scheduler_direct-assign-tooltip")}>
						<InfoOutlinedIcon
							sx={{ fontSize: "16px", color: "rgba(0, 0, 0, 0.56)" }}
						/>
					</Tooltip>
				)}
			</Box>
		),
		renderCell: (params: any) => {
			const color = params.row.restrictFlexHours
				? !params.row.timeExceeded
					? "#747474"
					: "#bdbdbd"
				: "#747474";
			const count = (
				<Typography variant='subtitle2' sx={{ color: color }}>
					{params.row.workersRequired}
				</Typography>
			);
			if (!canDirectAssign(params.row.locationId)) return count;

			const assignedCount = assignedCountByShift[params.row.id] ?? 0;
			return (
				<Box display='flex' alignItems='center' gap={1.5}>
					{assignedCount > 0 ? (
						<Box display='flex' flexDirection='column'>
							{count}
							<Typography
								sx={{
									fontSize: "12px",
									color: "#747474",
									whiteSpace: "nowrap",
								}}
							>
								{t("scheduler_n-assigned", { n: assignedCount })}
							</Typography>
						</Box>
					) : (
						count
					)}
					<Button
						variant={assignedCount > 0 ? "text" : "outlined"}
						size='small'
						disabled={!selectedShiftIds.includes(params.row.id)}
						sx={{ whiteSpace: "nowrap" }}
						// The row is clickable for selection; keep this click to itself
						onClick={(e) => {
							e.stopPropagation();
							onAssignWorkers(params.row.id);
						}}
					>
						{assignedCount > 0
							? t("common_edit")
							: t("scheduler_assign-workers")}
					</Button>
				</Box>
			);
		},
	},
	{
		field: "hoursScheduled",
		headerName: t("scheduler_total-hours"),
		minWidth: 150,
		flex: 1,
		renderCell: (params: any) => {
			const color = params.row.restrictFlexHours
				? !params.row.timeExceeded
					? "#747474"
					: "#bdbdbd"
				: "#747474";
			return (
				<Typography variant='subtitle2' sx={{ color: color }}>
					{params.row.hoursScheduled}
				</Typography>
			);
		},
	},
	{
		field: "createdByName",
		headerName: t("scheduler_created-by-header"),
		minWidth: 170,
		flex: 1,
		renderCell: (params: any) => {
			const color = params.row.restrictFlexHours
				? !params.row.timeExceeded
					? "#747474"
					: "#bdbdbd"
				: "#747474";
			return (
				<Typography variant='subtitle2' sx={{ color: color }}>
					{params.row.createdByName}
				</Typography>
			);
		},
	},
	{
		...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
		type: "string",
		renderCell: (params: any) => <></>,
	},
		],
		[
			t,
			hasDirectAssignPermission,
			canDirectAssign,
			assignedCountByShift,
			selectedShiftIds,
			onAssignWorkers,
		]
	);
};

export const useStyles = makeStyles((theme) => ({
	actionBar: {
		marginTop: 8,
		marginBottom: 8,
	},
	btnLabel: {
		position: "relative",
	},
	progressIndicator: {
		position: "absolute",
		left: "50%",
	},
	dataGridColumn: {
		"& .MuiDataGrid-columnSeparator--sideRight": {
			display: "none !important",
		},
		border: "none !important",
	},
}));

export const reducer = (state: number[], action: Action): number[] => {
	switch (action.type) {
		case "selectShiftForPublish":
			return [...state, action.payload];
		case "deselectShiftForPublish":
			return state.filter((item) => {
				return item !== action.payload;
			});
		case "setShiftsForPublish":
			return [...action.payload];
		default:
			return state;
	}
};
